import React from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Avatar from "@mui/material/Avatar";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";

import { Iconify } from "@onair/minimal-ui/iconify";

import { NotificationTypes, NotificationResponse } from "@onair/repositories";

import { BlogSvg } from "#src/components/svg/blog";
import { LiveSvg } from "#src/components/svg/live";
import { EventSvg } from "#src/components/svg/event";
import { CalenderSvg } from "#src/components/svg/calender";
import { RejectedSvg } from "#src/components/svg/rejected";
import { PublishedSvg } from "#src/components/svg/published";
import { AddFollowSvg } from "#src/components/svg/add-follow";
import { PaymentSuccessSvg } from "#src/components/svg/payment-success";

import { HostApprovedCalendar } from "../svg/host-approved-calendar";
import { HostRejectedCalendar } from "../svg/host-rejected-calendar";

interface Props {
	notification: NotificationResponse;
}

const ICON_MAPPING: Partial<Record<NotificationTypes, React.ReactNode>> = {
	[NotificationTypes.PAYMENT_SUCCESS]: <PaymentSuccessSvg />,
};

const COVER_ICON_MAPPING: Partial<Record<NotificationTypes, React.ReactNode>> =
	{
		[NotificationTypes.NEW_FOLLOWER]: (
			<Iconify icon="solar:user-bold-duotone" />
		),
		[NotificationTypes.CALENDAR_BOOKING_APPROVED]: (
			<Iconify icon="solar:user-bold-duotone" />
		),
		[NotificationTypes.CALENDAR_BOOKING_REJECTED]: (
			<Iconify icon="solar:user-bold-duotone" />
		),
	};

export const NotificationAvatar = ({ notification }: Props) => {
	let extraAvatarStyle = {};
	switch (notification.type) {
		case NotificationTypes.PROJECT_REJECTED:
		case NotificationTypes.BLOG_REJECTED:
		case NotificationTypes.EVENT_REJECTED:
		case NotificationTypes.CALENDAR_REJECTED:
			extraAvatarStyle = {
				border: "1px solid #FF2727",
			};
			break;
		default:
			break;
	}

	let secondaryIcon = null;
	let mainIcon = null;
	let secondaryExtraSx = {};
	switch (notification.type) {
		case NotificationTypes.FOLLOWING_HOST_NEW_EVENT:
			secondaryIcon = <EventSvg />;
			mainIcon = notification.icon;
			break;
		case NotificationTypes.NEW_FOLLOWER:
			secondaryIcon = <AddFollowSvg />;
			mainIcon = notification.icon;
			break;
		case NotificationTypes.BLOG_APPROVED:
		case NotificationTypes.PROJECT_APPROVED:
		case NotificationTypes.EVENT_APPROVED:
		case NotificationTypes.CALENDAR_APPROVED:
			secondaryIcon = <PublishedSvg />;
			mainIcon = notification.icon;
			break;
		case NotificationTypes.BLOG_REJECTED:
		case NotificationTypes.PROJECT_REJECTED:
		case NotificationTypes.EVENT_REJECTED:
		case NotificationTypes.CALENDAR_REJECTED:
			secondaryIcon = <RejectedSvg />;
			mainIcon = notification.icon;
			break;
		case NotificationTypes.FOLLOWING_HOST_NEW_BLOG:
		case NotificationTypes.FOLLOWING_HOST_NEW_PROJECT:
			secondaryIcon = <BlogSvg />;
			mainIcon = notification.icon;
			break;
		case NotificationTypes.FOLLOWING_HOST_NEW_CALENDAR:
			secondaryIcon = <CalenderSvg />;
			mainIcon = notification.icon;
			break;
		case NotificationTypes.APPROVED_HOST_PROFILE:
			secondaryIcon = null;
			mainIcon = "/assets/images/notification/host-approved.png";
			break;
		case NotificationTypes.REJECTED_HOST_PROFILE:
			secondaryIcon = null;
			mainIcon = "/assets/images/notification/host-rejected.png";
			break;
		case NotificationTypes.CALENDAR_BOOKING_APPROVED:
			secondaryIcon = <HostApprovedCalendar />;
			mainIcon = notification.icon;
			break;
		case NotificationTypes.CALENDAR_BOOKING_REJECTED:
			secondaryIcon = <HostRejectedCalendar />;
			mainIcon = notification.icon;
			break;
		case NotificationTypes.PAYMENT_SUCCESS:
		case NotificationTypes.PAYMENT_SUCCESS_FREE:
			secondaryIcon = null;
			mainIcon = "/assets/images/notification/payment-success.png";
			break;
		case NotificationTypes.PAYMENT_FAILED:
			secondaryIcon = null;
			mainIcon = "/assets/images/notification/payment-fail.png";
			break;
		case NotificationTypes.CALENDAR_STARTED:
		case NotificationTypes.EVENT_STARTED:
			secondaryIcon = <LiveSvg />;
			secondaryExtraSx = {
				width: 32,
				height: 16,
				bottom: -4,
				right: 3,
			};
			mainIcon = notification.icon;
			break;
		case NotificationTypes.CALENDAR_HOST_SUGGEST_APPROVED:
			secondaryIcon = null;
			mainIcon =
				"/assets/images/notification/calendar-host-suggest-approve.png";
			break;
		case NotificationTypes.CALENDAR_REMINDER_APPROVED:
			secondaryIcon = null;
			mainIcon =
				"/assets/images/notification/calendar-host-reminder-approve.png";
			break;
		case NotificationTypes.REFUND_SUCCESS:
			secondaryIcon = null;
			mainIcon = "/assets/images/notification/refund-success.png";
			break;
		case NotificationTypes.REFUND_FAILED:
			secondaryIcon = null;
			mainIcon = "/assets/images/notification/refund-failed.png";
			break;
		case NotificationTypes.EVENT_HOST_REMINDER:
		case NotificationTypes.CALENDAR_REMINDER:
			secondaryIcon = null;
			mainIcon = notification.icon;
			break;
		case NotificationTypes.ONLINE_COURSE_APPROVED:
			secondaryIcon = <PublishedSvg />;
			mainIcon = JSON.parse(notification.icon as string).thumbnailUrl;
			break;
		case NotificationTypes.ONLINE_COURSE_REJECTED:
			secondaryIcon = <RejectedSvg />;
			mainIcon = JSON.parse(notification.icon as string).thumbnailUrl;
			break;
		case NotificationTypes.MEMBERSHIP_PLAN_PRICE_CHANGED:
			secondaryIcon = null;
			mainIcon = "/assets/images/notification/membership-money.png";
			break;
		case NotificationTypes.MEMBERSHIP_PLAN_PERIOD_CHANGED:
		case NotificationTypes.MEMBERSHIP_PLAN_ALL_CHANGED:
			secondaryIcon = null;
			mainIcon = "/assets/images/notification/membership-calendar.png";
			break;
		case NotificationTypes.SUBSCRIPTION_CANCELLED:
			secondaryIcon = null;
			mainIcon = "/assets/images/notification/subscription-cancelled.png";
			break;
		case NotificationTypes.SUBSCRIPTION_FIRST_PAYMENT_FAILED:
			secondaryIcon = null;
			mainIcon =
				"/assets/images/notification/subscription-first-payment-failed.png";
			break;
		case NotificationTypes.SUBSCRIPTION_FIRST_PAYMENT_SUCCESS:
			secondaryIcon = null;
			mainIcon =
				"/assets/images/notification/subscription-first-payment-success.png";
			break;
		case NotificationTypes.SUBSCRIPTION_RENEWAL:
			secondaryIcon = null;
			mainIcon = "/assets/images/notification/subscription-renewal.png";
			break;
		case NotificationTypes.SUBSCRIPTION_RENEWAL_FAILED:
			secondaryIcon = null;
			mainIcon = "/assets/images/notification/subscription-renewal-failed.png";
			break;
		case NotificationTypes.SUBSCRIPTION_RENEWAL_SUCCESS:
			secondaryIcon = null;
			mainIcon = "/assets/images/notification/subscription-renewal-success.png";
			break;
		case NotificationTypes.SUBSCRIPTION_TIMEOUT:
			secondaryIcon = null;
			mainIcon = "/assets/images/notification/subscription-payment-timeout.png";
			break;
		case NotificationTypes.PAYMENT_TOKEN_DELETED:
			secondaryIcon = null;
			mainIcon = "/assets/images/notification/payment-token-deleted.png";
			break;
		case NotificationTypes.LAYOFF_DISCOUNT_APPROVED:
			secondaryIcon = null;
			mainIcon = "/assets/images/notification/layoff-discount-approved.png";
			break;
		case NotificationTypes.LAYOFF_DISCOUNT_REJECTED:
			secondaryIcon = null;
			mainIcon = "/assets/images/notification/layoff-discount-rejected.png";
			break;
		case NotificationTypes.LAYOFF_DISCOUNT_REQUESTED:
			secondaryIcon = null;
			mainIcon = "/assets/images/notification/layoff-discount-requested.png";
			break;
		case NotificationTypes.HOST_PRODUCT_SOLD:
			secondaryIcon = null;
			mainIcon = "/assets/images/notification/host-product-sold.png";
			break;
		case NotificationTypes.ONLINE_COURSE_UNLOCK:
			secondaryIcon = null;
			mainIcon = "/assets/images/notification/course-unlock.png";
			break;
		default:
			secondaryIcon = null;
			mainIcon = null;
			break;
	}

	// Tạo component Box nếu có secondaryIcon
	if (secondaryIcon) {
		secondaryIcon = (
			<Box
				className="rounded-full"
				sx={{
					width: 15,
					height: 15,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					position: "absolute",
					bottom: 0,
					right: 0,
					...secondaryExtraSx,
				}}
			>
				{secondaryIcon}
			</Box>
		);
	}

	return (
		<ListItemAvatar
			sx={{
				position: "relative",
			}}
		>
			{mainIcon && !ICON_MAPPING[notification.type] ? (
				<Avatar
					src={mainIcon}
					sx={{ bgcolor: "background.neutral", ...extraAvatarStyle }}
				/>
			) : (
				<Stack
					alignItems="center"
					justifyContent="center"
					sx={{
						width: 40,
						height: 40,
						borderRadius: "50%",
						bgcolor: "background.neutral",
					}}
				>
					{ICON_MAPPING[notification.type] ||
						COVER_ICON_MAPPING[notification.type] || (
							<NotificationsActiveIcon />
						)}
				</Stack>
			)}
			{secondaryIcon}
		</ListItemAvatar>
	);
};
